// The company a person is signing in to.
//
// On a tenant's own domain the host answers this, and nobody is asked anything.
// On the platform's shared host the URL says nothing, so the sign-in screen
// asks — "type your company, then sign in" — and the answer is remembered here
// so a returning user goes straight to Microsoft.
//
// This is a CONVENIENCE, never a permission. It decides which name and logo the
// sign-in screen shows and which directory Microsoft is pointed at; it grants
// nothing. What a signed-in user may actually see is decided by the backend from
// their verified token — see middleware/auth.ts and the tenant gate. Anyone can
// edit localStorage; that must remain boring, and it is.

// Brand-neutral on purpose: this is a white-label product, and other companies
// run it under their own name.
const KEY = 'workspace_choice';

export interface WorkspaceChoice {
  slug: string;
  name: string;
  app_name: string;
  logo_url: string | null;
  domain_hint: string | null;
}

function isChoice(v: unknown): v is WorkspaceChoice {
  const c = v as Partial<WorkspaceChoice> | null;
  return !!c && typeof c.slug === 'string' && !!c.slug && typeof c.name === 'string';
}

export function getWorkspaceChoice(): WorkspaceChoice | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isChoice(parsed) ? parsed : null;
  } catch {
    // Storage blocked or the value is junk. Falling back to "ask again" is
    // always safe; it costs one extra step and nothing else.
    return null;
  }
}

export function setWorkspaceChoice(choice: WorkspaceChoice): void {
  try { localStorage.setItem(KEY, JSON.stringify(choice)); } catch { /* private mode */ }
}

export function clearWorkspaceChoice(): void {
  try { localStorage.removeItem(KEY); } catch { /* private mode */ }
}

/**
 * Extra parameters for the Microsoft redirect, given the chosen company.
 *
 * `domain_hint` sends the user straight to their own organisation's sign-in
 * instead of the generic Microsoft account picker — the difference between
 * "which account?" and just signing in. It is a hint only: Microsoft still
 * authenticates them, and our backend still checks which directory the
 * resulting token came from.
 */
export function loginHintsFor(choice: WorkspaceChoice | null): Record<string, string> {
  return choice?.domain_hint ? { domain_hint: choice.domain_hint } : {};
}
